'use client';
import React from 'react';
import { Plus } from 'lucide-react';
import { BlockProps } from './types';
import { isBlockEmptyForProduct } from '../blockHelpers';
import { SpotlightOverlay } from '../shared/SpotlightOverlay';

export function ContainerBlock({
  block,
  currentStyles,
  viewport,
  isAdmin,
  isBuilderCanvas,
  selectedBlockId,
  onSelectBlock,
  product,
  category,
  onUpdateBlockStyle,
  onUpdateBlockContent,
  isNested = false,
  depth = 0,
  onDuplicateBlock,
  onMoveBlockUp,
  onMoveBlockDown,
  onRemoveBlock,
  initialBlockData,
  authMode,
  isFirstBlock,
  firstBlockBreakpoints
}: BlockProps) {
  if (!isAdmin && product && isBlockEmptyForProduct(block, product)) {
    return null;
  }

  const { BlockRenderer } = require('../BlockRenderer');

  // If container has children, render them seamlessly via BlockRenderer
  if (block.children && block.children.length > 0) {
    const renderedChildren = (
      <BlockRenderer
        blocks={block.children}
        viewport={viewport}
        product={product}
        category={category}
        selectedBlockId={selectedBlockId}
        onSelectBlock={onSelectBlock}
        onUpdateBlockStyle={onUpdateBlockStyle}
        onUpdateBlockContent={onUpdateBlockContent}
        isAdmin={isAdmin}
        isBuilderCanvas={isBuilderCanvas}
        isNested={true}
        depth={depth + 1}
        onDuplicateBlock={onDuplicateBlock}
        onMoveBlockUp={onMoveBlockUp}
        onMoveBlockDown={onMoveBlockDown}
        onRemoveBlock={onRemoveBlock}
        initialBlockData={initialBlockData}
        authMode={authMode}
        prioritizeFirstVisible={isFirstBlock ? (firstBlockBreakpoints ?? true) : false}
      />
    );

    let content: React.ReactNode = renderedChildren;

    // A child pulled up with a negative top margin ("wystaje na sekcję wyżej") must overlap the
    // previous section. Without a new block formatting context that margin collapses through this
    // section instead and drags the whole section — background included — up over its neighbour.
    const pullsUp = block.children.some((child: any) => {
      const s = child?.styles || {};
      return [s, s.desktop, s.tablet, s.mobile].some((v: any) => parseFloat(v?.marginTop) < 0);
    });
    if (pullsUp) content = <div className="flow-root">{content}</div>;

    // Spacing between the blocks inside (Układ sekcji → Odstęp elementów).
    const childGap = currentStyles?.childGap;
    if (childGap !== undefined && childGap !== null && childGap !== '' && Number(childGap) > 0) {
      content = <div className="flex flex-col" style={{ gap: `${Number(childGap)}px` }}>{content}</div>;
    }

    // How wide the content may get inside the section. Without it: a nested block always fills
    // 100% of its parent (see BlockRenderer's isNested check), normally correct since the parent
    // already applied the page's reading width — but a TOP-LEVEL container with "Pełna szerokość"
    // skips that constraint (so its background can bleed edge-to-edge), so the max-w-7xl
    // constraint is re-added for it, without extra padding on top of the container's own.
    const contentMaxWidth = Number(currentStyles?.contentMaxWidth) || 0;
    if (contentMaxWidth > 0) {
      content = <div className="w-full mx-auto" style={{ maxWidth: `${contentMaxWidth}px` }}>{content}</div>;
    } else if (!isNested && currentStyles?.fullWidth === true) {
      content = <div className="w-full max-w-7xl mx-auto">{content}</div>;
    }

    // A colour wash over the section's background picture, under its content (positioned against
    // BlockRenderer's relative wrapper, so it covers the padding too, like the background).
    const overlayOpacity = Math.min(100, Math.max(0, Number(currentStyles?.overlayOpacity) || 0));
    const spotlight = currentStyles?.spotlightEnabled === true;
    if (overlayOpacity > 0 || spotlight) {
      return (
        <>
          {overlayOpacity > 0 && (
            <div
              aria-hidden="true"
              className="absolute inset-0 pointer-events-none"
              style={{ backgroundColor: currentStyles?.overlayColor || '#000000', opacity: overlayOpacity / 100, borderRadius: 'inherit' }}
            />
          )}
          {spotlight && (
            <SpotlightOverlay
              color={currentStyles?.spotlightColor || '#000000'}
              opacity={Number(currentStyles?.spotlightOpacity ?? 85)}
              size={Number(currentStyles?.spotlightSize) || 420}
              restY={Number(currentStyles?.spotlightY ?? 42)}
              isAdmin={isAdmin}
            />
          )}
          <div className="relative">{content}</div>
        </>
      );
    }
    return <>{content}</>;
  }

  // Admin empty container placeholder
  if (isAdmin) {
    return (
      <div
        onClick={(e) => {
          e.stopPropagation();
          if ((window as any).__prepareAdd) {
            (window as any).__prepareAdd(block.id);
          }
        }}
        className="my-3 min-h-[80px] border-2 border-dashed border-slate-300/80 hover:border-blue-400 rounded-xl flex items-center justify-center gap-2 text-slate-400 hover:text-blue-600 bg-slate-50/50 hover:bg-blue-50/20 text-[11px] font-bold uppercase tracking-wider transition-all cursor-pointer select-none"
      >
        <Plus className="w-4 h-4 opacity-70" />
        <span>Pusta sekcja ({block.customName || 'Sekcja'}) – kliknij, aby dodać zawartość</span>
      </div>
    );
  }

  return null;
}
